import { useState } from "react";
import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pencil, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface Meal {
  meal_time?: string;
  foods: string[];
  calories: number | null;
}

interface MealPlanFood {
  type: "meal_plan";
  event_date: string;
  text: string;
  risk_level: string;
  created_at: string;
  plan_name: string;
  start_date: string;
  is_main_record: string | boolean;
  structure_type: "food";
  day_of_week?: string;
  meal_type?: string;
  meals: Meal[];
  notes?: string | null;
}

interface SmartfileSession {
  id: string;
  file_name: string;
  content_type: string | null;
  ai_summary?: string | null;
  created_at: string;
  structured_records?: any[] | null;
  patient_id: string | null;
  status: string | null;
}

interface SmartfileMealPlanFoodDetailsProps {
  session: SmartfileSession;
  onRefresh: () => void;
  onRecordsChange?: (updatedRecords: any[]) => void;
  expandAllByDefault?: boolean;
}

interface EditableMealPlanFood extends MealPlanFood {
  _index?: number;
}

// Mapear meal_type para label
const getMealTypeLabel = (mealType?: string): string => {
  if (!mealType) return 'Refeição';
  return mealType.toUpperCase();
};

// Ordenar tipos de refeição
const getMealTypeOrder = (mealType?: string): number => {
  const order: Record<string, number> = {
    'CAFÉ DA MANHÃ': 1,
    'CAFE DA MANHA': 1,
    'ALMOÇO': 2,
    'ALMOCO': 2,
    'LANCHE': 3,
    'JANTAR': 4,
    'CEIA': 5,
  };
  return order[mealType?.toUpperCase() || ''] || 999;
};

// Type guard para verificar se é um meal_plan food válido
function isFoodMealPlan(record: any): record is MealPlanFood {
  if (typeof record !== 'object' || record === null) return false;
  if (!('type' in record) || record.type !== 'meal_plan') return false;
  const mealPlan = record as { structure_type?: string; is_main_record?: boolean | string };
  const isMainRecord = mealPlan.is_main_record === true || mealPlan.is_main_record === 'true';
  return mealPlan.structure_type === 'food' && !isMainRecord;
}

export function SmartfileMealPlanFoodDetails({ session, onRefresh, onRecordsChange, expandAllByDefault = false }: SmartfileMealPlanFoodDetailsProps) {
  // Buscar o main_record
  const mainRecord = (session.structured_records || []).find((record: any) =>
    typeof record === 'object' &&
    record !== null &&
    'type' in record &&
    record.type === 'meal_plan' &&
    ((record as any).is_main_record === true || (record as any).is_main_record === 'true')
  ) as MealPlanFood | undefined;

  const allRecords = session.structured_records || [];
  console.log(`🔍 [SmartfileMealPlanFoodDetails] Session ${session.id}: ${allRecords.length} total records`);

  const mealPlans: EditableMealPlanFood[] = allRecords
    .filter(isFoodMealPlan)
    .map((record: MealPlanFood, index: number) => ({
      ...record,
      _index: index
    }));

  console.log(`✅ [SmartfileMealPlanFoodDetails] Filtered ${mealPlans.length} meal_plans (food) from ${allRecords.length} records`);

  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [editingMainRecord, setEditingMainRecord] = useState(false);
  const [editedRecords, setEditedRecords] = useState<EditableMealPlanFood[]>(mealPlans);
  const [editedMainRecord, setEditedMainRecord] = useState<MealPlanFood | undefined>(mainRecord);
  const [isSaving, setIsSaving] = useState(false);

  // Atualizar editedRecords quando mealPlans mudar
  React.useEffect(() => {
    setEditedRecords(mealPlans);
    setEditedMainRecord(mainRecord);
  }, [session.structured_records, mainRecord]);

  // Extrair dias únicos e ordená-los
  const allDays = ['SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB', 'DOM'];
  const presentDays = new Set(mealPlans.map(p => p.day_of_week).filter(Boolean) as string[]);
  const sortedDays = allDays.filter(day => presentDays.has(day));

  // Extrair tipos de refeição únicos e ordená-los
  const mealTypes = Array.from(new Set(mealPlans.map(p => p.meal_type).filter(Boolean) as string[]))
    .sort((a, b) => getMealTypeOrder(a) - getMealTypeOrder(b));

  // Função para obter o plano de um dia e tipo de refeição específicos
  const getPlanForDayAndMeal = (day: string, mealType: string): EditableMealPlanFood | undefined => {
    return mealPlans.find(p => p.day_of_week === day && p.meal_type === mealType);
  };

  const handleSaveRow = async (mealType: string) => {
    if (!editingRow || editingRow !== mealType) return;

    setIsSaving(true);
    try {
      const updatedRecords = [...(session.structured_records || [])];
      const plansToSave: Array<{ planIndex: number; realPlanIndex: number }> = [];

      // Encontrar todos os planos deste tipo de refeição
      sortedDays.forEach(day => {
        const plan = getPlanForDayAndMeal(day, mealType);
        if (!plan) return;

        const planIndex = editedRecords.findIndex(p => p._index === plan._index);
        if (planIndex === -1) return;

        // Encontrar o índice real do plano no array completo
        let mealPlanCount = 0;
        let realPlanIndex = -1;

        for (let i = 0; i < updatedRecords.length; i++) {
          const record = updatedRecords[i];
          if (isFoodMealPlan(record)) {
            if (mealPlanCount === planIndex) {
              realPlanIndex = i;
              break;
            }
            mealPlanCount++;
          }
        }

        if (realPlanIndex !== -1) {
          plansToSave.push({ planIndex, realPlanIndex });
        }
      });

      // Atualizar todos os planos deste tipo de refeição
      plansToSave.forEach(({ planIndex, realPlanIndex }) => {
        const updatedPlan = { ...editedRecords[planIndex] };
        updatedRecords[realPlanIndex] = updatedPlan;
      });

      if (plansToSave.length > 0) {
        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        // Chamar onRecordsChange se fornecido (para Step2Analyze)
        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success(`✅ ${getMealTypeLabel(mealType)} atualizado com sucesso em ${plansToSave.length} dia(s)!`);
        setEditingRow(null);
        onRefresh();
      } else {
        toast.error("❌ Nenhum plano encontrado para salvar");
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      toast.error("❌ Erro ao salvar alterações");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveMainRecord = async () => {
    if (!editedMainRecord) return;

    setIsSaving(true);
    try {
      const updatedRecords = [...(session.structured_records || [])];
      const mainRecordIndex = updatedRecords.findIndex((record: any) =>
        typeof record === 'object' &&
        record !== null &&
        'type' in record &&
        record.type === 'meal_plan' &&
        ((record as any).is_main_record === true || (record as any).is_main_record === 'true')
      );

      if (mainRecordIndex >= 0) {
        updatedRecords[mainRecordIndex] = editedMainRecord;

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Título do plano atualizado com sucesso!");
        setEditingMainRecord(false);
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      toast.error("❌ Erro ao salvar alterações");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteCell = async (day: string, mealType: string) => {
    try {
      const updatedRecords = [...(session.structured_records || [])];
      const plan = getPlanForDayAndMeal(day, mealType);

      if (!plan) {
        toast.error("❌ Plano não encontrado");
        return;
      }

      const planIndex = editedRecords.findIndex(p => p._index === plan._index);
      if (planIndex === -1) {
        toast.error("❌ Plano não encontrado");
        return;
      }

      // Encontrar o índice real do plano no array completo
      let mealPlanCount = 0;
      let realPlanIndex = -1;

      for (let i = 0; i < updatedRecords.length; i++) {
        const record = updatedRecords[i];
        if (isFoodMealPlan(record)) {
          if (mealPlanCount === planIndex) {
            realPlanIndex = i;
            break;
          }
          mealPlanCount++;
        }
      }

      if (realPlanIndex !== -1) {
        updatedRecords.splice(realPlanIndex, 1);

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        // Chamar onRecordsChange se fornecido (para Step2Analyze)
        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Refeição excluída com sucesso!");
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao excluir:', error);
      toast.error("❌ Erro ao excluir refeição");
    }
  };

  const handleCancel = () => {
    setEditedRecords(mealPlans);
    setEditingRow(null);
  };

  if (mealPlans.length === 0) {
    return (
      <div className="p-4 text-center text-sm text-muted-foreground">
        Nenhum plano alimentar (food) encontrado.
      </div>
    );
  }

  return (
    <div className="bg-background border-t">
      {/* Header com título editável */}
      {mainRecord && (
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1">
              {editingMainRecord ? (
                <div className="flex items-center gap-3 flex-1">
                  <div className="flex items-center gap-2 flex-1">
                    <Label className="text-xs">Título:</Label>
                    <Input
                      value={editedMainRecord?.plan_name || ''}
                      onChange={(e) => {
                        setEditedMainRecord({
                          ...editedMainRecord!,
                          plan_name: e.target.value
                        });
                      }}
                      className="h-8 text-sm"
                      placeholder="Nome do plano"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <h3 className="text-lg font-bold">
                    {mainRecord?.plan_name || 'Plano Alimentar'}
                  </h3>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              {editingMainRecord ? (
                <>
                  <Button
                    size="sm"
                    onClick={handleSaveMainRecord}
                    disabled={isSaving}
                    className="h-8"
                  >
                    {isSaving ? "Salvando..." : "Salvar"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditingMainRecord(false);
                      setEditedMainRecord(mainRecord);
                    }}
                    disabled={isSaving}
                    className="h-8"
                  >
                    Cancelar
                  </Button>
                </>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEditingMainRecord(true);
                  }}
                  className="h-8"
                >
                  <Pencil className="h-4 w-4 mr-1" />
                  Editar
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="p-4 overflow-x-auto">
        <div className="min-w-full inline-block">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b border-r bg-muted/50 sticky left-0 z-10 min-w-[120px]">
                  Refeição
                </th>
                {sortedDays.map((day) => (
                  <th
                    key={day}
                    className="p-3 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b bg-muted/50 min-w-[200px]"
                  >
                    {day}
                  </th>
                ))}
                <th className="p-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b bg-muted/50 min-w-[100px]">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody>
              {mealTypes.map((mealType) => {
                const isEditingThisRow = editingRow === mealType;

                return (
                  <tr key={mealType} className="border-b border-border hover:bg-muted/30 transition-colors">
                    <td className="p-3 text-sm font-medium align-top border-r bg-muted/20 sticky left-0 z-10">
                      {getMealTypeLabel(mealType)}
                    </td>
                    {sortedDays.map((day) => {
                      const plan = getPlanForDayAndMeal(day, mealType);

                      if (!plan) {
                        return (
                          <td key={day} className="p-3 text-sm text-muted-foreground/50 border-r">
                            –
                          </td>
                        );
                      }

                      const planIndex = editedRecords.findIndex(p => p._index === plan._index);
                      const meal = plan.meals[0]; // Cada registro food tem apenas uma refeição

                      return (
                        <td key={day} className="p-3 text-sm border-r align-top">
                          {isEditingThisRow ? (
                            <div className="space-y-2 p-2 bg-muted/30 rounded border">
                              <div>
                                <Label className="text-xs">Alimentos</Label>
                                <Input
                                  value={editedRecords[planIndex]?.meals[0]?.foods?.join(', ') || ''}
                                  onChange={(e) => {
                                    const updated = [...editedRecords];
                                    updated[planIndex] = {
                                      ...updated[planIndex],
                                      meals: [{
                                        ...updated[planIndex].meals[0],
                                        foods: e.target.value.split(',').map(f => f.trim()).filter(Boolean)
                                      }]
                                    };
                                    setEditedRecords(updated);
                                  }}
                                  className="h-8 text-xs mt-1"
                                  placeholder="Ex: 1 xícara de arroz integral"
                                />
                              </div>
                              <div className="flex gap-1">
                                <Input
                                  type="number"
                                  value={editedRecords[planIndex]?.meals[0]?.calories ?? ''}
                                  onChange={(e) => {
                                    const updated = [...editedRecords];
                                    updated[planIndex] = {
                                      ...updated[planIndex],
                                      meals: [{
                                        ...updated[planIndex].meals[0],
                                        calories: e.target.value ? parseFloat(e.target.value) : null
                                      }]
                                    };
                                    setEditedRecords(updated);
                                  }}
                                  className="h-8 text-xs flex-1"
                                  placeholder="Calorias"
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="text-xs">
                              {meal.foods && meal.foods.length > 0 ? (
                                meal.foods.join(', ')
                              ) : (
                                <span className="text-muted-foreground">–</span>
                              )}
                            </div>
                          )}
                        </td>
                      );
                    })}
                    <td className="p-3 text-right align-top">
                      {isEditingThisRow ? (
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="sm"
                            onClick={() => handleSaveRow(mealType)}
                            disabled={isSaving}
                            className="h-7 px-2"
                          >
                            {isSaving ? "Salvando..." : "Salvar"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleCancel}
                            disabled={isSaving}
                            className="h-7 px-2"
                          >
                            Cancelar
                          </Button>
                        </div>
                      ) : (
                        <div className="flex gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingRow(mealType)}
                            className="h-7 px-2"
                            title={`Editar todas as refeições de ${getMealTypeLabel(mealType)}`}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                title={`Excluir todas as refeições de ${getMealTypeLabel(mealType)}`}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Tem certeza que deseja excluir todas as refeições de {getMealTypeLabel(mealType)}?
                                  <br />
                                  <span className="font-medium mt-2 block text-xs">
                                    Esta ação excluirá todas as refeições deste tipo em todos os dias.
                                  </span>
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={async () => {
                                    // Excluir todos os planos deste tipo de refeição
                                    for (const day of sortedDays) {
                                      const plan = getPlanForDayAndMeal(day, mealType);
                                      if (plan) {
                                        await handleDeleteCell(day, mealType);
                                      }
                                    }
                                    onRefresh();
                                  }}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Excluir
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <div className="p-3 text-xs text-muted-foreground border-t border-border bg-muted/30">
        Total: {mealPlans.length} refeição(ões) em {sortedDays.length} dia(s)
      </div>
    </div>
  );
}

